import { Injectable, SecurityContext } from '@angular/core';
import { cloneDeep, sortBy } from 'lodash';
import { CompanyIcpService } from 'src/app/components/company-icp/company-icp.service';
import { BehaviorSubject, ReplaySubject, Subscription, Subject } from 'rxjs';

import { TrackerService } from 'src/app/components/logging/tracker.service';
import { AIChatbotSession } from '../../models/ai-chatbot-session';
import { AIChatbotAttributeData, AIChatbotMessage, AIChatbotMessageContent } from '../../models/ai-chatbot-message';
import { DomSanitizer } from '@angular/platform-browser';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { PromiseUtilsService } from 'src/app/shared/services/promise-utils.service';
import { AiChatbotPresentationData } from '../../models/ai-chatbot-presentation-data';
import { AIChatbotApiService } from '../ai-chatbot-api/ai-chatbot-api.service';
import { AIChatbotInterpretationResponse } from '../../models/ai-chatbot-interpretation-response';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '@uirouter/angular';

@Injectable({
  providedIn: 'root',
})
export class AIChatbotService {
  styles = [
    {
      name: 'Modern (clean, minimal, trendy)',
      value: 'modern',
    },
    {
      name: 'Classic (serif fonts, formal)',
      value: 'classic',
    },
    {
      name: 'Playful (bright, fun, kid friendly)',
      value: 'playful',
    },
    {
      name: 'Bold (high contrast)',
      value: 'bold',
    },
    {
      name: 'Vibrant (colorful)',
      value: 'vibrant',
    },
    {
      name: 'Professional (corporate, clean)',
      value: 'professional',
    },
    {
      name: 'Calm (pastel tones, relaxed)',
      value: 'calm',
    },
  ];

  contents = [
    {
      icon: 'content-video',
      name: 'Video',
      value: 'video',
    },
    {
      icon: 'content-video-streaming',
      name: 'Video Streaming',
      value: 'video-streaming',
    },
    {
      icon: 'content-playlist',
      name: 'Playlist',
      value: 'playlist',
    },
    {
      icon: 'content-date-time',
      name: 'Date & Time',
      value: 'date-time',
    },
    {
      icon: 'content-countdown-up',
      name: 'Countdown & up',
      value: 'countdown-up',
    },
    {
      icon: 'content-youtube',
      name: 'Youtube',
      value: 'youtube',
    },
    {
      icon: 'content-weather',
      name: 'Weather',
      value: 'weather',
    },
    {
      icon: 'content-rss-news',
      name: 'RSS News',
      value: 'rss-news',
    },
    {
      icon: 'content-screen-share-code',
      name: 'Screen Share Code',
      value: 'screen-share-code',
    },
    {
      icon: 'content-canva',
      name: 'Canva',
      value: 'canva',
    },
  ];

  industries = sortBy(
    CompanyIcpService.COMPANY_INDUSTRY_FIELDS.map((industry) => ({
      name: industry[0],
      value: industry[1],
      selected: false,
    })),
    (industry) => industry.name
  );

  errorMessages = [
    'The task failed… successfully! 👍 Please try again.',
    '👀 Oops, something went wrong. Please try again.',
    '💥🤖💥 Sorry, I hit a little glitch. Please try again.',
    "🙈 I'm sorry... I got distracted. Try again?",
    '⛔️ Error! The developers dropped the ball on this one! Try again.',
    "⛔️ Error! The design team didn't design for this scenario. Try again.",
    "⚙️ Oh no! I tripped over my own gears. I'm rebooting — try again.",
    "🤔 Hmm, that didn't work as expected. Want to try again?",
    'I accidentally sent your request into space. Waiting for it to come back. Want to try again?',
  ];

  firstMessages: string[] = [
    `Hi %firstName%! I'm Pixel 👋`,
    `\nI'm excited to help you build your perfect presentation.`,
    `\n\nTo give you the best design, could you tell me a bit about:<br><b>• Style</b> (e.g., modern, playful, professional)<br><b>• Content</b> (e.g., announcements, schedules, promotions)<br><b>• Industry</b> (e.g., education, retail, healthcare)`,
    `\n\nReady when you are!`,
  ];
  firstMessagesShownParts: string[] = [];
  firstMessageIsRendering = true;

  isProcessing = false;
  private _isLoadingTemplates = false;
  get isLoadingTemplates(): boolean {
    return this._isLoadingTemplates;
  }
  set isLoadingTemplates(value: boolean) {
    this._isLoadingTemplates = value;
    this.isLoadingTemplates$.next(value);

    if (value && !this.interpretationResponse) {
      this.showLoadingSamples$.next();
    }
  }
  isLoadingTemplates$ = new BehaviorSubject<boolean>(false);
  interpretationResponse?: AIChatbotInterpretationResponse;
  templatesResponse?: AIChatbotMessage['content'];

  showLoadingSamples$ = new Subject<void>();
  scrollChatToBottom$ = new ReplaySubject<{ onlyIfAtBottom?: boolean }>(1);
  restartSession$ = new Subject<void>();

  presentationEdit$ = new Subject<AIChatbotAttributeData>();

  private cancelRequest$ = new Subject<void>();

  currentSession?: AIChatbotSession;
  private messagesSubscription: Subscription;

  constructor(
    private trackerService: TrackerService,
    private userStateService: UserStateService,
    private promiseUtilsService: PromiseUtilsService,
    private aiChatbotApiService: AIChatbotApiService,
    private sanitizer: DomSanitizer,
    private stateService: StateService,
    private dialog: MatDialog
  ) {}

  startNewSession(): AIChatbotSession {
    const sessionId = this.generateSessionId();
    this.currentSession = {
      id: sessionId,
      messages$: new BehaviorSubject<AIChatbotMessage[]>([]),
      presentation: null,
      variationId: 1,
      excludedProductCodes: [],
      latestPrompt: '',
      latestConcept: '',
      latestActions: '',
      latestTemplates: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Subscribe to messages changes
    this.messagesSubscription = this.currentSession.messages$.subscribe(() => {
      if (this.currentSession) {
        this.currentSession.updatedAt = new Date();
      }
      this.scrollChatToBottom$.next({});
    });

    this.generateFirstMessage();

    this.trackerService.presentationEvent(
      'AI Chatbot Session Started',
      sessionId
    );

    this.restartSession$.next();

    return this.currentSession;
  }

  async sendMessage(userMessage: AIChatbotMessage): Promise<void> {
    if (!this.currentSession) {
      this.startNewSession();
    }

    if (this.isProcessing) {
      throw new Error('Already processing a message');
    }

    this.isProcessing = true;
    this.interpretationResponse = undefined;

    const currentMessages = this.currentSession.messages$.getValue();
    let resetVariationId = false;
    let requestBrighterColors = false;

    // If is resending a canceled message
    if (userMessage.isNotSuccessful) {
      userMessage.isNotSuccessful = false;

      // If it is not the last message, copy the message
      if (userMessage.id !== currentMessages[currentMessages.length - 1].id) {
        userMessage = cloneDeep(userMessage);
        userMessage.id = this.generateMessageId();
        this.currentSession.messages$.next([...currentMessages, userMessage]);
      }
    } else {
      this.currentSession.messages$.next([...currentMessages, userMessage]);
    }

    try {
      this.trackerService.presentationEvent(
        'AI Chatbot Message Sent',
        this.currentSession.id,
        userMessage
      );

      const latestPrompt = this.currentSession.latestPrompt;
      let messageString = userMessage.content.message;
      let aiMessage: string;
      let interpretRequired = true;

      if (messageString.toLowerCase() === 'more presentations') {
        if (latestPrompt) {
          userMessage.content.displayMessage = messageString;
          userMessage.content.message = latestPrompt;
          messageString = latestPrompt;
        } else {
          this.addMessageAndRefresh(this.createAssistantTextMessage('What kind of templates are you looking for?'));
          interpretRequired = false;
        }
      } else if (messageString.toLowerCase() === 'brighter colors') {
        if (latestPrompt) {
          requestBrighterColors = true;
          userMessage.content.displayMessage = messageString;
          userMessage.content.message = latestPrompt;
          messageString = latestPrompt;
        } else {
          this.addMessageAndRefresh(this.createAssistantTextMessage('You need to search for templates first.'));
        }

        interpretRequired = false;
      } else {
        resetVariationId = true;
      }

      if (interpretRequired) {
        let recursiveCalls = 0;

        const runInterpretationStep = async () => {
          this.interpretationResponse = await this.aiChatbotApiService.interpret(
            this.currentSession,
            messageString,
            userMessage.content,
            this.cancelRequest$
          );

          recursiveCalls++;
        }

        const evaluateInterpretationResponse = async () => {
          if (this.interpretationResponse.type === 'chat') {
            aiMessage = this.interpretationResponse.data as unknown as string;

          } else if (this.interpretationResponse.type === 'explicit_material') {
            aiMessage =
              "👀 Whoa there! Pixel keeps it clean.\n\nI can't generate anything explicit—but I've got your back!\nI'll go ahead and create something tasteful and creative that's within my limits.";

          } else if (this.interpretationResponse.type === 'unsupported') {
            aiMessage =
              "🤖 Pixel here! That one's outside my skillset (for now).\n\nBut no worries—I'll whip up something creative and similar that's within my capabilities!";

          } else if (this.interpretationResponse.type === 'select_presentation' || this.interpretationResponse.type === 'template_select') {
            aiMessage = `Got it! Using ${this.interpretationResponse.data.name}.`;

          } else if (this.interpretationResponse.type === 'presentation_edit') {
            aiMessage = `Got it! Applying changes.`;

          } else if (this.interpretationResponse.type === 'request_brighter_colors') {
            aiMessage = `Got it! Using brighter colors.`;
            requestBrighterColors = true;
          } else if (this.interpretationResponse.type === 'request_more') {
            userMessage.content.displayMessage = messageString;
            userMessage.content.message = latestPrompt;
            messageString = latestPrompt;

            if (recursiveCalls < 2) {
              await runInterpretationStep();
              await evaluateInterpretationResponse();
            }
          }
        };

        await runInterpretationStep();
        await evaluateInterpretationResponse();

        if (aiMessage) {
          let messageContent;

          if (this.interpretationResponse.type === 'select_presentation' || this.interpretationResponse.type === 'template_select') {
            messageContent = {
              type: 'message',
              message: aiMessage,
              data: aiMessage
            };

            this.selectPresentation(this.interpretationResponse.data);

          } else if (this.interpretationResponse.type === 'presentation_edit') {
            messageContent = {
              type: 'message',
              message: aiMessage,
              data: aiMessage
            };

            this.currentSession.presentation.attributeData = this.interpretationResponse.data;

          } else {
            messageContent = {
              type: 'message',
              message: aiMessage,
              data: aiMessage
            };
          }

          this.addMessageAndRefresh(this.createAssistantMessage(messageContent));

          if (
            this.interpretationResponse.type === 'unsupported' ||
            this.interpretationResponse.type === 'explicit_material'
          ) {
            this.interpretationResponse.type = 'generating_presentations';
          }
        }
      }

      const responseType = this.interpretationResponse?.type;
      const generateRequired = responseType === 'template_search' || requestBrighterColors;

      if (generateRequired) {
        let generationResponse: AIChatbotMessageContent = null;

        if (responseType === 'template_search') {
          this.showLoadingSamples$.next();
          this.scrollChatToBottom$.next({});

          this.isLoadingTemplates = true;

          if (resetVariationId) {
            this.currentSession.variationId = 1;
            this.currentSession.excludedProductCodes = [];

            this.currentSession.latestPrompt = messageString;
          }

          const baseTemplates = this.interpretationResponse.data.templates || this.currentSession.latestTemplates;
          const updatedTemplates = await this.generateMultipleVariations(baseTemplates, this.interpretationResponse.data.query);

          this.interpretationResponse.data.templates = updatedTemplates;
          this.currentSession.latestTemplates = updatedTemplates;
          this.currentSession.latestConcept = this.interpretationResponse.data.query;
          this.currentSession.latestActions = this.interpretationResponse.data.actions;
          this.currentSession.excludedProductCodes = [
            ...this.currentSession.excludedProductCodes,
            ...updatedTemplates.map(t => t.productCode)
          ];

          this.isLoadingTemplates = false;

          generationResponse = {
            type: 'presentations',
            data: {
              query: this.currentSession.latestConcept,
              actions: this.currentSession.latestActions,
              templates: updatedTemplates
            }
          };
        } else if (requestBrighterColors) {
          this.currentSession.variationId++;

          if (this.currentSession.presentation) {
            const newPresentation = await this.generateSinglePresentationColors(this.currentSession.variationId, this.currentSession.presentation);

            console.error('New colors for presentation', newPresentation);

            this.currentSession.presentation = newPresentation;
          } else {
            const newPresentations = await this.generateMultiplePresentationColors(this.currentSession.variationId, this.currentSession.latestTemplates);

            console.error('New colors for presentations', newPresentations);

            generationResponse = {
              type: 'presentations',
              data: {
                query: this.currentSession.latestConcept,
                actions: this.currentSession.latestActions,
                templates: newPresentations
              }
            };
          }
        } else {
          // Simulate API generate call
          generationResponse = await this.aiChatbotApiService.generate(
            userMessage.content.message,
            this.currentSession.messages$.getValue(),
            this.cancelRequest$,
            this.interpretationResponse,
            this.currentSession.id
          );
        }

        this.addMessageAndRefresh(generationResponse ? this.createAssistantMessage(generationResponse) : null);
      }

      this.isProcessing = false;

      // Track AI response
      this.trackerService.presentationEvent(
        'AI Chatbot Response Received',
        this.currentSession.id
      );
    } catch (error) {
      // Ensure UI returns to idle state on any error
      this.isProcessing = false;
      this.isLoadingTemplates = false;
      this.interpretationResponse = undefined;

      userMessage.isNotSuccessful = true;

      if (error === 'processing_canceled') {
        return;
      }

      const errorMessage: AIChatbotMessage = {
        id: this.generateMessageId(),
        timestamp: new Date(),
        role: 'error',

        content: {
          type: 'message',
          message:
            this.errorMessages[
              Math.floor(Math.random() * this.errorMessages.length)
            ],
        },
      };

      const currentMessages = this.currentSession.messages$.getValue();
      this.currentSession.messages$.next([...currentMessages, errorMessage]);

      this.trackerService.presentationEvent(
        'AI Chatbot Error',
        this.currentSession.id,
        error instanceof Error ? error.message : String(error)
      );

      throw error;
    }
  }

  async generateMultipleVariations(presentations: AiChatbotPresentationData[], concept?: string): Promise<AiChatbotPresentationData[]> {
    const variationConcept = concept || this.currentSession.latestConcept;
    const promises = presentations.map(p => this.generateSingleVariation(p, variationConcept));

    return await Promise.all(promises);
  }

  async generateSingleVariation(presentation: AiChatbotPresentationData, concept: string): Promise<AiChatbotPresentationData> {
    const newAttributeData = await this.aiChatbotApiService.generatePresentationVariation(
      this.currentSession.id,
      presentation.productCode,
      concept,
      presentation.result?.semanticScore || 100,
      this.cancelRequest$);

    return { ...presentation, attributeData: newAttributeData };
  }

  async generateSinglePresentationColors(variationId: number, presentation: AiChatbotPresentationData): Promise<AiChatbotPresentationData> {
    const sessionId = this.currentSession.id;
    const attributeData = presentation.attributeData;
    const newAttributeData = await this.aiChatbotApiService.generatePresentationColors(sessionId, variationId, attributeData, this.cancelRequest$);

    return { ...presentation, attributeData: newAttributeData };
  }

  async generateMultiplePresentationColors(variationId: number, presentations: AiChatbotPresentationData[]): Promise<AiChatbotPresentationData[]> {
    const promises = presentations.map(p => this.generateSinglePresentationColors(variationId, p));

    return Promise.all(promises);
  }

  async selectPresentation(presentation: AiChatbotPresentationData): Promise<void> {
    const currentMessages = this.currentSession.messages$.getValue();
    const previewIdx = currentMessages.findIndex(m => m.content.type === 'single_presentation');

    if (previewIdx !== -1) {
      currentMessages.splice(previewIdx, 1);
    }

    const userMessage: AIChatbotMessage = {
      id: this.generateMessageId(),
      timestamp: new Date(),
      role: 'user',
      content: {
        type: 'message',
        message: `Use ${presentation.name}`,
      },
    };

    this.currentSession.presentation = presentation;

    this.currentSession.messages$.next([...currentMessages, userMessage]);

    const aiMessage: AIChatbotMessage = {
      id: this.generateMessageId(),
      timestamp: new Date(),
      role: 'assistant',
      content: {
        type: 'single_presentation',
        message: `Got it! Using ${presentation.name}.`,
        data: presentation
      },
    };

    this.currentSession.messages$.next([...currentMessages, aiMessage]);
  }

  addMessageAndRefresh(aiChatbotMessage?: AIChatbotMessage) {
    const presentation = this.currentSession.presentation;
    const currentMessages = this.currentSession.messages$.getValue();
    const idx = currentMessages.findIndex(m => m.content.type === 'single_presentation');

    let selectedPresentationMsg: AIChatbotMessage = null;

    if (idx !== -1) {
      selectedPresentationMsg = currentMessages[idx];
      selectedPresentationMsg.content.message = '';

      currentMessages.splice(idx, 1);
    }

    const newMessages = [
      ...currentMessages,
      ...(aiChatbotMessage ? [aiChatbotMessage] : []),
      ...(selectedPresentationMsg ? [selectedPresentationMsg] : [])
    ];

    this.currentSession.messages$.next(newMessages);
    this.scrollChatToBottom$.next({});

    if (selectedPresentationMsg && presentation?.attributeData) {
      this.presentationEdit$.next(presentation.attributeData);
    }
  }

  openPresentationInEditor(presentation: AiChatbotPresentationData) {
    this.dialog.closeAll();
    this.restartSession();

    return this.stateService.go('apps.editor.templates.edit', {
      presentationId: 'new',
      productDetails: presentation,
      attributeData: presentation.attributeData,
      trackingData: {}
    });
  }

  async generateFirstMessage(): Promise<void> {
    this.firstMessagesShownParts = [];
    this.firstMessageIsRendering = true;

    // Sanitize firstName in case a malicious user defined
    // his first name with a string that could lead to XSS attacks
    const safeName =
      this.sanitizer.sanitize(
        SecurityContext.HTML,
        this.userStateService._state.profile.firstName
      ) || '';

    for (let i = 0; i < this.firstMessages.length; i++) {
      await this.promiseUtilsService.delay(500);
      this.firstMessagesShownParts.push(
        this.firstMessages[i].replaceAll('%firstName%', safeName)
      );
      this.scrollChatToBottom$.next({});
    }

    await this.promiseUtilsService.delay(500);
    this.firstMessageIsRendering = false;
  }

  stopProcessing(): void {
    this.isProcessing = false;
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
    this.cancelRequest$ = new Subject<void>();
  }

  clearSession(): void {
    if (this.currentSession) {
      this.trackerService.presentationEvent(
        'AI Chatbot Session Cleared',
        this.currentSession.id
      );
    }

    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
      this.messagesSubscription = null;
    }

    this.interpretationResponse = undefined;
    this.currentSession = undefined;
    this.isProcessing = false;
    this.scrollChatToBottom$.next({});
  }

  restartSession(): void {
    this.clearSession();
    this.startNewSession();
  }

  private generateSessionId(): string {
    return (
      'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    );
  }

  generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  createAssistantMessage(content: AIChatbotMessageContent): AIChatbotMessage {
    return {
      id: this.generateMessageId(),
      timestamp: new Date(),
      role: 'assistant',
      content
    };
  }

  createAssistantTextMessage(message: string): AIChatbotMessage {
    return this.createAssistantMessage({
      type: 'message',
      message,
      data: message
    });
  }
}
